import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./styles.css";
import { CmdAuthProvider, useCmdAuth } from "./contexts/CmdAuthContext";
import { CmdThemeProvider } from "./contexts/CmdThemeContext";
import CmdLogin from "./pages/CmdLogin";
import CmdFounderSetup from "./pages/CmdFounderSetup";
import CmdShell from "./components/CmdShell";
import CmdPlaceholder from "./pages/CmdPlaceholder";
import CmdPortfolio from "./pages/CmdPortfolio";
import CmdBoard from "./pages/CmdBoard";
import CmdDecisions from "./pages/CmdDecisions";
import CmdFinancials from "./pages/CmdFinancials";
import CmdReview from "./pages/CmdReview";
import CmdReports from "./pages/CmdReports";
import CmdAccess from "./pages/CmdAccess";
import CmdAcceptInvite from "./pages/CmdAcceptInvite";
import CmdSettings from "./pages/CmdSettings";
import CmdInsights from "./pages/CmdInsights";
import CmdCapital from "./pages/CmdCapital";
import { usePermissions } from "./hooks/usePermissions";

function RequireAuth({ children }) {
  const { me, setupNeeded } = useCmdAuth();
  const loc = useLocation();
  if (setupNeeded === null || me === null) return null; // boot flicker
  if (setupNeeded) return <Navigate to="/cmd/setup" replace />;
  if (!me) return <Navigate to="/cmd/login" state={{ from: loc }} replace />;
  return children;
}

function RequireModule({ module, children }) {
  const { visible } = usePermissions();
  if (!visible(module)) return <Navigate to="/cmd" replace />;
  return children;
}

function RequireFounder({ children }) {
  const { isFounder } = usePermissions();
  if (!isFounder) return <Navigate to="/cmd" replace />;
  return children;
}

function CmdHome() {
  const { visible, isFounder } = usePermissions();
  // Deep-link to the first tab the user is allowed to see.
  const order = ["portfolio", "board", "decisions", "financials", "reports", "review"];
  for (const m of order) if (visible(m)) return <Navigate to={`/cmd/${m}`} replace />;
  if (isFounder) return <Navigate to="/cmd/access" replace />;
  return (
    <CmdPlaceholder
      title="Welcome"
      hint="Your account has no module access yet. Ask the Founder to assign your role in Access."
      testId="cmd-home-empty"
    />
  );
}

export default function CmdApp() {
  return (
    <CmdThemeProvider>
      <CmdAuthProvider>
        <Routes>
          <Route path="/login" element={<CmdLogin />} />
          <Route path="/setup" element={<CmdFounderSetup />} />
          <Route path="/accept-invite" element={<CmdAcceptInvite />} />
          <Route
            path="/*"
            element={
              <RequireAuth>
                <CmdShell>
                  <Routes>
                    <Route index element={<CmdHome />} />
                    <Route path="portfolio"  element={<RequireModule module="portfolio"><CmdPortfolio /></RequireModule>} />
                    <Route path="board"      element={<RequireModule module="board"><CmdBoard /></RequireModule>} />
                    <Route path="decisions"  element={<RequireModule module="decisions"><CmdDecisions /></RequireModule>} />
                    <Route path="financials" element={<RequireModule module="financials"><CmdFinancials /></RequireModule>} />
                    <Route path="reports"    element={<RequireModule module="reports"><CmdReports /></RequireModule>} />
                    <Route path="insights"   element={<RequireModule module="financials"><CmdInsights /></RequireModule>} />
                    <Route path="capital"    element={<RequireModule module="capital"><CmdCapital /></RequireModule>} />
                    <Route path="review"     element={<RequireModule module="review"><CmdReview /></RequireModule>} />
                    <Route path="access"     element={<RequireFounder><CmdAccess /></RequireFounder>} />
                    <Route path="settings"   element={<RequireFounder><CmdSettings /></RequireFounder>} />
                    <Route path="*" element={<Navigate to="/cmd" replace />} />
                  </Routes>
                </CmdShell>
              </RequireAuth>
            }
          />
        </Routes>
      </CmdAuthProvider>
    </CmdThemeProvider>
  );
}
